// The /create studio's "Type words" (text to speech) and "AI song"
// (Treblo generation) forms, in design language F. They report exactly what
// TextToSpeechInputView and GenerateSongView do (same fields, same limits,
// same options), so the request body is unchanged.
import React, { useState } from "react";
import toast from "react-hot-toast";
import ReactCountryFlag from "react-country-flag";
import {
  accents as ACCENTS_JS,
  expressions as EXPRESSIONS_JS,
  genders as GENDERS_JS,
} from "@/components/modals/TextToSpeechInputView";
import {
  GENRE_PRESETS as GENRE_PRESETS_JS,
  LYRICS_PLACEHOLDER,
  MAX_CAPTION,
  MAX_LYRICS,
} from "@/components/modals/GenerateSongView";

/* eslint-disable @typescript-eslint/no-explicit-any */

const GENDERS = GENDERS_JS as { displayName: string; value: string }[];
const ACCENTS = ACCENTS_JS as { displayName: string; flagCode: string }[];
const EXPRESSIONS = EXPRESSIONS_JS as { displayName: string; emoji: string }[];
const GENRE_PRESETS = GENRE_PRESETS_JS as { label: string; caption: string }[];
const MAX_TTS = 2000;

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`tw-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold ${
      active
        ? "tw-bg-jam-ink tw-text-white"
        : "tw-bg-jam-lav tw-text-jam-ink hover:tw-text-jam-purple"
    }`}
  >
    {children}
  </button>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="tw-text-xs tw-font-extrabold tw-tracking-[0.04em] tw-text-jam-muted">
    {children}
  </div>
);

const TextBox = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...props}
    className="tw-w-full tw-resize-none tw-rounded-[22px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-4 tw-text-[15px] tw-font-semibold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted focus:tw-border-jam-purple"
  />
);

const UseButton = ({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="tw-h-12 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[15px] tw-font-extrabold tw-text-white disabled:tw-cursor-not-allowed disabled:tw-bg-jam-soft"
  >
    {children}
  </button>
);

export const TypeWords = ({
  selectedVoices,
  onConfirm,
}: {
  selectedVoices: any[];
  onConfirm: (tts: {
    text: string;
    accent: string;
    gender: string;
    expression: string;
  }) => void;
}) => {
  const [text, setText] = useState("");
  // Same default as TextToSpeechInputView: the lead voice's TTS gender.
  const [gender, setGender] = useState(
    selectedVoices?.[0]?.tts?.gender === "female" ? "female" : "male",
  );
  const [accent, setAccent] = useState(ACCENTS[0].flagCode);
  const [expression, setExpression] = useState(EXPRESSIONS[0].displayName);

  const confirm = () => {
    if (!text.trim()) {
      toast.error("Please enter some text.");
      return;
    }
    if (text.length > MAX_TTS) {
      toast.error(`Text exceeds the maximum limit of ${MAX_TTS} characters.`);
      return;
    }
    onConfirm({ text: text.trim(), accent, gender, expression });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3.5">
      <TextBox
        aria-label="What should they say?"
        placeholder="Type what they should say..."
        rows={4}
        value={text}
        maxLength={MAX_TTS + 50}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>BASE VOICE</Label>
        <div className="tw-flex tw-gap-2">
          {GENDERS.map((g) => (
            <Chip
              key={g.value}
              active={gender === g.value}
              onClick={() => setGender(g.value)}
            >
              {g.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>ACCENT</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {ACCENTS.map((a) => (
            <Chip
              key={a.flagCode}
              active={accent === a.flagCode}
              onClick={() => setAccent(a.flagCode)}
            >
              <ReactCountryFlag countryCode={a.flagCode} svg />
              {a.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>DELIVERY</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {EXPRESSIONS.map((x) => (
            <Chip
              key={x.displayName}
              active={expression === x.displayName}
              onClick={() => setExpression(x.displayName)}
            >
              <span>{x.emoji}</span>
              {x.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <UseButton onClick={confirm} disabled={!text.trim()}>
        Use these words
      </UseButton>
    </div>
  );
};

export const AiSong = ({
  voiceModelName,
  onConfirm,
}: {
  voiceModelName: string;
  onConfirm: (g: {
    caption: string;
    lyrics: string;
    voice_model_name: string;
  }) => void;
}) => {
  const [caption, setCaption] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [ownLyrics, setOwnLyrics] = useState(false);

  const confirm = () => {
    if (!caption.trim()) {
      toast.error("Describe the style of the song.");
      return;
    }
    if (caption.length > MAX_CAPTION) {
      toast.error(`Style description must be under ${MAX_CAPTION} characters.`);
      return;
    }
    if (lyrics.length > MAX_LYRICS) {
      toast.error(`Lyrics must be under ${MAX_LYRICS} characters.`);
      return;
    }
    onConfirm({
      caption: caption.trim(),
      lyrics: lyrics.trim(),
      voice_model_name: voiceModelName || "",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3.5">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>PICK A STYLE, OR DESCRIBE ONE</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {GENRE_PRESETS.map((g) => (
            <Chip
              key={g.label}
              active={caption === g.caption}
              onClick={() => setCaption(g.caption)}
            >
              {g.label}
            </Chip>
          ))}
        </div>
      </div>
      <TextBox
        aria-label="Song style"
        placeholder="e.g. upbeat summer pop, acoustic guitar, catchy chorus"
        rows={2}
        value={caption}
        maxLength={MAX_CAPTION}
        onChange={(e) => setCaption(e.target.value)}
      />
      {ownLyrics ? (
        <TextBox
          aria-label="Lyrics"
          placeholder={LYRICS_PLACEHOLDER}
          rows={6}
          value={lyrics}
          maxLength={MAX_LYRICS}
          onChange={(e) => setLyrics(e.target.value)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setOwnLyrics(true)}
          className="tw-cursor-pointer tw-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
        >
          + Write your own lyrics (otherwise AI writes them)
        </button>
      )}
      <UseButton onClick={confirm} disabled={!caption.trim()}>
        Use this song
      </UseButton>
      {/* Required by Treblo's API terms: a prominent, legible link to
          treblo.com wherever generation is offered. Do not remove or shrink
          it without a waiver from api@treblo.com. */}
      <a
        href="https://treblo.com"
        target="_blank"
        rel="noopener noreferrer"
        className="tw-self-center tw-text-sm tw-font-bold tw-text-jam-muted tw-underline tw-underline-offset-2 hover:tw-text-jam-purple"
      >
        Powered by Treblo
      </a>
    </div>
  );
};
